import { useState, useEffect, useRef } from 'react';
import { WalletHeader } from '../components/WalletHeader';
import { PlanCards } from '../components/PlanCards';
import { UsageFilters } from '../components/UsageFilters';
import { UsageTable, UsageRecord } from '../components/UsageTable';

// Mock Data with ISO dates for filtering
const BASE_DATE = new Date();
const getPastDate = (daysAgo: number) => {
    const d = new Date(BASE_DATE);
    d.setDate(d.getDate() - daysAgo);
    return d.toISOString();
};

/**
 * Histórico de consumo del plan — eventos plausibles para un restaurante
 * usando Deenex AI (envíos WhatsApp, push, segmentaciones con IA, etc.).
 *
 * "Incluido" = entró en el cupo mensual del plan
 * "On-Demand" = cargo extra por exceder el cupo o por feature premium
 */
const MOCK_DATA: UsageRecord[] = [
    {
        id: "1",
        date: getPastDate(0),
        displayDate: "Hoy, 16:39",
        type: "Incluido",
        model: "Envío WhatsApp · Carrito abandonado",
        tokens: "234 mensajes",
        cost: "Incluido",
    },
    {
        id: "2",
        date: getPastDate(0),
        displayDate: "Hoy, 14:12",
        type: "Incluido",
        model: "Push notification · Promoción del día",
        tokens: "1.842 envíos",
        cost: "Incluido",
    },
    {
        id: "3",
        date: getPastDate(1),
        displayDate: "Ayer, 18:30",
        type: "Incluido",
        model: "Segmentación con IA · Inactivos 30d",
        tokens: "12.450 perfiles analizados",
        cost: "Incluido",
    },
    {
        id: "4",
        date: getPastDate(2),
        displayDate: "Hace 2 días",
        type: "On-Demand",
        model: "Envío WhatsApp · Reactivación VIPs",
        tokens: "1.156 mensajes",
        cost: "$8.40",
    },
    {
        id: "5",
        date: getPastDate(5),
        displayDate: "Hace 5 días",
        type: "Incluido",
        model: "Generación de plantillas con IA",
        tokens: "8 plantillas creadas",
        cost: "Incluido",
    },
    {
        id: "6",
        date: getPastDate(8),
        displayDate: "Hace 8 días",
        type: "On-Demand",
        model: "Envío WhatsApp · Cumpleaños · Locales Centro",
        tokens: "342 mensajes",
        cost: "$2.50",
    },
    {
        id: "7",
        date: getPastDate(15),
        displayDate: "Hace 15 días",
        type: "Incluido",
        model: "Análisis de marca con IA (BrandLearning)",
        tokens: "1 dominio + 1 PDF",
        cost: "Incluido",
    },
    {
        id: "8",
        date: getPastDate(32),
        displayDate: "Hace 1 mes",
        type: "On-Demand",
        model: "Envío WhatsApp · Promo aniversario",
        tokens: "4.821 mensajes",
        cost: "$35.20",
    },
];

export const WalletPage = () => {
    // State
    const [dateRange, setDateRange] = useState<{ start: Date | null, end: Date | null }>({
        start: new Date(new Date().setDate(new Date().getDate() - 30)), // Default 30d
        end: new Date()
    });
    const [activeFilter, setActiveFilter] = useState<'1d' | '7d' | '30d' | 'custom'>('30d');
    const [filteredData, setFilteredData] = useState<UsageRecord[]>(MOCK_DATA);
    const [isLoading, setIsLoading] = useState(false);

    // Ref para evitar setState en componente desmontado (memory leak fix)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    // Filter Logic
    const handleFilterChange = (type: '1d' | '7d' | '30d' | 'custom', range?: { start: Date | null, end: Date | null }) => {
        setIsLoading(true);
        setActiveFilter(type);

        let newStart: Date | null = null;
        let newEnd: Date | null = new Date(); // Default end is today

        if (type === 'custom' && range) {
            setDateRange(range);
            newStart = range.start;
            newEnd = range.end;
        } else {
            // Quick filters
            newStart = new Date();
            if (type === '1d') newStart.setDate(newStart.getDate() - 1);
            if (type === '7d') newStart.setDate(newStart.getDate() - 7);
            if (type === '30d') newStart.setDate(newStart.getDate() - 30);

            setDateRange({ start: newStart, end: newEnd });
        }

        // Simulate API/Filtering delay — usando ref para cleanup seguro
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (newStart && newEnd) {
                // Normalize dates for comparison (ignore time)
                const startLimit = new Date(newStart); startLimit.setHours(0, 0, 0, 0);
                const endLimit = new Date(newEnd); endLimit.setHours(23, 59, 59, 999);

                const filtered = MOCK_DATA.filter(item => {
                    const itemDate = new Date(item.date);
                    return itemDate >= startLimit && itemDate <= endLimit;
                });
                setFilteredData(filtered);
            }
            setIsLoading(false);
        }, 600); // 600ms fake loading
    };

    // Initial Filter validation (ensure data matches default 30d)
    useEffect(() => {
        // Runs once to ensure default state is consistent if we change initial state
        handleFilterChange('30d');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-7xl space-y-6">
                <WalletHeader />
                <PlanCards />

                <div className="space-y-6">
                    <UsageFilters
                        dateRange={dateRange}
                        activeFilter={activeFilter}
                        onFilterChange={handleFilterChange}
                    />
                    <UsageTable
                        data={filteredData}
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </div>
    );
};

export default WalletPage;
